import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../../../redux/store";

const API_BASE = process.env.REACT_APP_API_URL || "";

export interface NotificationStats {
  devices: number;
  ios: number;
  android: number;
}

export interface BroadcastPayload {
  title: string;
  body: string;
  data?: Record<string, unknown>;
  image?: File | null;
}

interface NotificationState {
  stats: NotificationStats | null;
  loading: boolean;
  sending: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  stats: null,
  loading: false,
  sending: false,
  error: null,
};

async function handleResp(res: Response) {
  const text = await res.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!res.ok) {
    const msg =
      (payload && (payload.message || payload.error)) ||
      res.statusText ||
      "Request failed";
    throw new Error(msg);
  }
  return payload;
}

export const fetchNotificationStats = createAsyncThunk<
  NotificationStats,
  void,
  { rejectValue: string; state: RootState }
>("notifications/stats", async (_, { rejectWithValue, getState }) => {
  const token = (getState() as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/notifications/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload: any = await handleResp(res);
    return payload.data as NotificationStats;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to load stats");
  }
});

export const broadcastNotification = createAsyncThunk<
  { message: string; recipients: number },
  BroadcastPayload,
  { rejectValue: string; state: RootState }
>("notifications/broadcast", async (payload, { rejectWithValue, getState }) => {
  const token = (getState() as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const form = new FormData();
    form.append("title", payload.title);
    form.append("body", payload.body);
    if (payload.data) {
      Object.entries(payload.data).forEach(([key, value]) => {
        form.append(`data[${key}]`, String(value));
      });
    }
    if (payload.image) {
      form.append("image", payload.image);
    }

    // Let the browser set the multipart Content-Type (with boundary).
    const res = await fetch(`${API_BASE}/notifications/broadcast`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    return await handleResp(res);
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to send notification");
  }
});

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchNotificationStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to load stats";
      })
      .addCase(broadcastNotification.pending, (state) => {
        state.sending = true;
        state.error = null;
      })
      .addCase(broadcastNotification.fulfilled, (state) => {
        state.sending = false;
      })
      .addCase(broadcastNotification.rejected, (state, action) => {
        state.sending = false;
        state.error = action.payload ?? "Failed to send notification";
      });
  },
});

export default notificationSlice.reducer;
